import { useBemCN } from "@gik/core/utils/bemBlock";
import {
	extractYouTubeVideoIdFromURL,
	isYouTubeUrl,
} from "@gik/core/utils/extractVideoId";
import noop from "@gik/core/utils/noop";
import coreTranslationKeys from "@gik/i18n/en/core";
import {
	createUserUpdate,
	updateUserUpdate,
	updateUserUpdateMedia,
} from "@gik/inkind-page/api/UserUpdatesAPI";
import { getVimeoThumbnailUrl } from "@gik/inkind-page/api/VimeoAPI";
import { ShareUpdateOnSocialMedia } from "@gik/inkind-page/components/AddEditInkindUserUpdatesModal/ShareUpdateOnSocialMedia/ShareUpdateOnSocialMedia";
import { AddUserUpdateInputNames } from "@gik/inkind-page/enums/AddUserUpdateInputNames";
import { AddVideoInputNames } from "@gik/inkind-page/enums/AddVideoInputNames";
import {
	addUserUpdateSchema,
	addVideoFormSchema,
} from "@gik/inkind-page/formSchemas";
import { translationKeys } from "@gik/inkind-page/i18n/en";
import type InkindUserUpdate from "@gik/inkind-page/models/InkindUserUpdate";
import type InkindUserUpdateMedia from "@gik/inkind-page/models/InkindUserUpdateMedia";
import { Button } from "@gik/ui-legacy/Button";
import { EmbeddedVideo } from "@gik/ui-legacy/EmbeddedVideo";
import type { FormSchemaEntry } from "@gik/ui-legacy/Form";
import { Form, FormField } from "@gik/ui-legacy/Form";
import { Image } from "@gik/ui-legacy/Image";
import type { IImagePickerRef } from "@gik/ui-legacy/ImagePicker";
import { ImagePicker } from "@gik/ui-legacy/ImagePicker";
import { KeepProportions } from "@gik/ui-legacy/KeepProportions";
import { LoadingSpinner } from "@gik/ui-legacy/LoadingSpinner";
import { SvgIcon } from "@gik/ui-legacy/SvgIcon/SvgIcon";
import { UI } from "@gik/ui-legacy/UIManager";
import CloseIcon from "@heroicons/react/solid/XIcon";
import he from "he";
import moment from "moment";
import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import useList from "react-use/lib/useList";

interface IAddEditInkindUserUpdatesModalProps {
	routeId?: string;
	isOpen?: boolean;
	onClose?: () => void;
	selectedUpdate?: InkindUserUpdate;
	buttonsPortal?: React.MutableRefObject<HTMLElement>;
}

const blockName = "add-edit-inkind-user-updates-modal";

export interface IAddEditInkindUserUpdatesForm {
	[AddUserUpdateInputNames.DATE]: number;
	[AddUserUpdateInputNames.TITLE]: string;
	[AddUserUpdateInputNames.CONTENT]: string;
	[AddUserUpdateInputNames.SEND_OPTIONS_PAGE]: boolean;
	[AddUserUpdateInputNames.SEND_OPTIONS_EMAIL]: boolean;
	[AddUserUpdateInputNames.SEND_OPTIONS_FACEBOOK]: boolean;
	[AddUserUpdateInputNames.SEND_OPTIONS_PAGE]: boolean;
}

export default function AddEditInkindUserUpdatesModal({
	routeId,
	onClose = noop,
	selectedUpdate,
	buttonsPortal,
}: IAddEditInkindUserUpdatesModalProps) {
	const bem = useBemCN(blockName);
	const { t } = useTranslation();

	const [value, setValue] = React.useState<
		Partial<IAddEditInkindUserUpdatesForm>
	>(
		selectedUpdate
			? inkindUserUpdateToAddEditInkindUserUpdatesForm(selectedUpdate)
			: {
					[AddUserUpdateInputNames.DATE]: Date.now(),
					[AddUserUpdateInputNames.SEND_OPTIONS_PAGE]: true,
					[AddUserUpdateInputNames.SEND_OPTIONS_EMAIL]: true,
				},
	);

	const [loading, setLoading] = React.useState<boolean>(false);
	const [uploadedMedia, uploadedMediaHandler] = useList<InkindUserUpdateMedia>(
		selectedUpdate?.media || [],
	);
	const mediaToBeRemoved = React.useRef<string[]>([]);
	const caption = React.useRef<string>();
	const videoLink = React.useRef<string>();
	const imagePickerRef = React.useRef<IImagePickerRef>(null);

	const schema = addUserUpdateSchema();

	// TODO: move this
	async function getThumbnailUrl(videoLink: string) {
		if (isYouTubeUrl(videoLink)) {
			return `https://img.youtube.com/vi/${extractYouTubeVideoIdFromURL(videoLink)}/default.jpg`;
		} else {
			return getVimeoThumbnailUrl(videoLink);
		}
	}

	function inkindUserUpdateToAddEditInkindUserUpdatesForm(
		update: InkindUserUpdate,
	): Partial<IAddEditInkindUserUpdatesForm> {
		return {
			[AddUserUpdateInputNames.DATE]: update.publishedDate
				? moment(update.publishedDate).valueOf()
				: Date.now(),
			[AddUserUpdateInputNames.TITLE]: update.title
				? he.decode(update.title)
				: null,
			[AddUserUpdateInputNames.CONTENT]: update.description
				? he.decode(update.description)
				: null,
			[AddUserUpdateInputNames.SEND_OPTIONS_PAGE]: true,
		};
	}

	const shareRef = React.useRef<HTMLDivElement>();
	const shareCloseRef = React.useRef<() => boolean>();

	async function handlePostUpdate() {
		setLoading(true);
		if (selectedUpdate) {
			await updateUserUpdate(routeId, selectedUpdate.id, value);
			await updateUserUpdateMedia(
				routeId,
				selectedUpdate.id,
				mediaToBeRemoved.current,
				uploadedMedia,
			);
		} else {
			const updateId = await createUserUpdate(routeId, value, uploadedMedia);

			if (
				value[AddUserUpdateInputNames.SEND_OPTIONS_FACEBOOK] ||
				value[AddUserUpdateInputNames.SEND_OPTIONS_TWITTER]
			) {
				await UI.dialog(
					<ShareUpdateOnSocialMedia
						updateId={updateId}
						shareOnFacebook={
							value[AddUserUpdateInputNames.SEND_OPTIONS_FACEBOOK]
						}
						shareOnTwitter={value[AddUserUpdateInputNames.SEND_OPTIONS_TWITTER]}
						sharedToEmail={value[AddUserUpdateInputNames.SEND_OPTIONS_EMAIL]}
						buttonsRef={shareRef}
						closeFnRef={shareCloseRef}
					/>,
					{
						title: t(translationKeys.ShareUpdateOnSocialMediaTitle),
						buttons: <div ref={shareRef} />,
						closable: true,
						shouldCloseOnOverlayClick: false,
						onClose: () => {
							if (shareCloseRef?.current) {
								return shareCloseRef.current();
							} else {
								return true;
							}
						},
					},
				);
			}
		}
		UI.closeDialog();
		setLoading(false);
		onClose();
	}

	async function openAddMediaModal() {
		let close: Function;
		await UI.dialog(
			({ close: _close }) => {
				close = _close;
				return (
					<div {...bem("add-media-modal")}>
						<ImagePicker
							{...bem("image-picker")}
							ref={imagePickerRef}
							aspect={1}
							noPreview
							addButtonText={t(translationKeys.AddMediaModalAddButtonText)}
							onChange={(croppedImage) =>
								croppedImage?.raster &&
								openConfirmImageModal(croppedImage.raster, _close)
							}
							crop={false}
						/>
						<Button onClick={() => openAddVideoModal(_close)}>
							Video from YouTube or Vimeo
						</Button>
					</div>
				);
			},
			{
				title: t(translationKeys.AddMediaModalAddButtonTitle),
				closable: true,
				...bem("add-media-modal-wrapper"),
				buttons: () => (
					<div>
						<Button variant={"default"} onClick={() => close?.()}>
							Cancel
						</Button>
					</div>
				),
			},
		);

		close?.();
	}

	async function openAddVideoModal(closeAddMediaModal: Function) {
		if (
			await UI.confirm(
				<div {...bem("add-video-modal")}>
					<p>Enter video link</p>

					<Form
						schema={addVideoFormSchema}
						onChange={(values) =>
							(videoLink.current = values[AddVideoInputNames.VIDEO_LINK])
						}
						initialValues={{
							[AddVideoInputNames.VIDEO_LINK]: videoLink.current,
						}}
					/>

					<p>
						To use a video from your phone, upload it to YouTube or Vimeo first.
					</p>
				</div>,
				{
					okText: t(coreTranslationKeys.core.continue),
					cancelText: t(coreTranslationKeys.core.goBack),
					...bem("add-video-modal-wrapper"),
				},
			)
		) {
			await openConfirmVideoModal(closeAddMediaModal);
			videoLink.current = undefined;
			closeAddMediaModal?.();
		}
	}

	async function openConfirmImageModal(
		croppedImage: string,
		closeAddMediaModal: Function,
	) {
		if (
			await UI.confirm(
				<CaptionFormModal
					{...bem("confirm-image-modal")}
					onChange={(value) => {
						caption.current = value;
					}}
				>
					<div {...bem("image-container")}>
						<Image alt={"Cropped image"} src={croppedImage} />
					</div>
				</CaptionFormModal>,
				{
					title: t(translationKeys.ConfirmImageModalTitle),
					okText: t(coreTranslationKeys.core.continue),
					cancelText: t(coreTranslationKeys.core.goBack),
					...bem("confirm-image-modal-wrapper"),
				},
			)
		) {
			uploadedMediaHandler.push({
				type: "photo",
				url: croppedImage,
				caption: caption.current,
			});
			caption.current = undefined;
			closeAddMediaModal?.();
		} else {
			imagePickerRef.current?.remove();
		}
	}

	async function openConfirmVideoModal(closeAddMediaModal: Function) {
		if (
			await UI.confirm(
				<CaptionFormModal
					{...bem("confirm-video-modal")}
					onChange={(value) => {
						caption.current = value;
					}}
				>
					<KeepProportions {...bem("video-container")}>
						<EmbeddedVideo url={videoLink.current?.trim()} />
					</KeepProportions>
				</CaptionFormModal>,
				{
					title: t(translationKeys.ConfirmVideoModalTitle),
					okText: t(coreTranslationKeys.core.continue),
					cancelText: t(coreTranslationKeys.core.goBack),
					...bem("confirm-video-modal-wrapper"),
				},
			)
		) {
			uploadedMediaHandler.push({
				type: "video",
				url: videoLink.current?.trim(),
				caption: caption.current,
				thumbnailUrl: await getThumbnailUrl(videoLink.current?.trim()),
			});
			closeAddMediaModal?.();
		}
		videoLink.current = undefined;
		caption.current = undefined;
	}

	function handleRemoveAttachment(i: number) {
		if (selectedUpdate) {
			mediaToBeRemoved.current?.push(uploadedMedia?.[i].id);
			// media to be added will not have id so we can check against that in the api call
		}
		uploadedMediaHandler.remove(i);
	}

	// TODO disable on invalid form
	function buttons(valid: boolean, submitting: boolean) {
		const buttonsDisabled = submitting || !valid;

		function onClick() {
			if (!buttonsDisabled) {
				handlePostUpdate();
			}
		}

		return (
			<Button
				type="submit"
				disabled={buttonsDisabled}
				loading={submitting}
				onClick={onClick}
			>
				{t(
					selectedUpdate
						? translationKeys.EditUserUpdateModalButtonLabel
						: translationKeys.AddUserUpdateModalButtonLabel,
				).toString()}
			</Button>
		);
	}

	return (
		<div {...bem(null, [{ loading }])}>
			{loading ? (
				<LoadingSpinner />
			) : (
				<>
					<Form
						schema={schema}
						initialValues={value}
						onChange={(values: any) => {
							setValue(values);
						}}
						vertical
						id="user-update-form"
						restoreAfterUpdate
						render={({ isValid, isSubmitting }) => {
							return (
								<>
									{schema.slice(0, 3).map((entry, key) => {
										return <FormField key={key} name={entry.name} />;
									})}

									<div {...bem("attachments")}>
										{uploadedMedia?.map((media, i) => (
											<Attachment
												key={i + media.url}
												media={media}
												removeAttachment={() => handleRemoveAttachment(i)}
											/>
										))}
									</div>

									{uploadedMedia?.length < 3 ? (
										<Button onClick={openAddMediaModal}>
											Add photo or video
										</Button>
									) : (
										<div>three attachments max.</div>
									)}

									{!selectedUpdate &&
										schema.slice(3).map((entry, key) => {
											return <FormField key={key} name={entry.name} />;
										})}

									{buttonsPortal?.current ? (
										ReactDOM.createPortal(
											buttons(isValid, isSubmitting),
											buttonsPortal.current,
										)
									) : (
										<div {...bem("button-wrapper")}>
											{buttons(isValid, isSubmitting)}
										</div>
									)}
								</>
							);
						}}
					/>
				</>
			)}
		</div>
	);
}

interface IAttachmentProps {
	media: InkindUserUpdateMedia;
	removeAttachment: () => void;
}

function Attachment({ media, removeAttachment }: IAttachmentProps) {
	const bem = useBemCN(blockName + "-attachment");

	function getAttachment() {
		switch (media.type) {
			case "photo":
				return (
					<Image
						{...bem("attachment", [{ [media.type]: true }])}
						src={media.url}
						alt={media.caption}
					/>
				);
			case "video":
				return (
					<Image
						{...bem("attachment", [{ [media.type]: true }])}
						src={media.thumbnailUrl}
						alt={media.caption}
					/>
				);
		}
	}

	return (
		<div {...bem()}>
			<div {...bem("remove-button")}>
				<Button
					variant={"danger"}
					size={"xs"}
					circle
					tooltip={"Remove attachment"}
					onClick={removeAttachment}
				>
					<SvgIcon Icon={CloseIcon} />
				</Button>
			</div>
			{getAttachment()}
		</div>
	);
}

interface ICaptionFormModalProps {
	className?: string;
	onChange?: (value: string) => void;
}

function CaptionFormModal({
	className,
	onChange = noop,
	children,
}: React.PropsWithChildren<ICaptionFormModalProps>) {
	const captionFieldName = "caption";

	const schema: FormSchemaEntry[] = [
		{
			name: captionFieldName,
			placeholder: "Caption",
			type: "text",
		},
	];

	function handleChange(value: { [captionFieldName]: string }) {
		onChange(value[captionFieldName]);
	}

	return (
		<div className={className}>
			{children}
			<Form
				onChange={handleChange}
				schema={schema}
				render={() => {
					return (
						<>
							{schema.map((field, key) => (
								<FormField key={key} name={field.name} />
							))}
						</>
					);
				}}
			/>
		</div>
	);
}
